import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  addDoc,
  deleteDoc,
  serverTimestamp,
  writeBatch,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase';
import { sortAutomationRows } from './automationTemplatesService';

/**
 * Agent templates (Technology ▸ Agents). One doc per agent in `agentTemplates`:
 * name, description, category (department), a display GIF in Storage, and five
 * ordered item lists — routines, abilities, library, skills, examples — each
 * `{ id, title, detail, skillId? }`. Skills items may link a Skills catalog doc
 * (`claudeSkills`) via skillId. Versions snapshot the whole doc.
 */
const COLLECTION = 'agentTemplates';
const SETTINGS_COLLECTION = 'agentTemplatesSettings';
const SETTINGS_CONFIG_ID = 'config';
const MAX_GIF_BYTES = 15 * 1024 * 1024;

export const AGENT_DEPARTMENTS = [
  { id: 'lead', label: 'Lead', emoji: '🎯', blurb: 'Agents for lead capture, follow-up, and nurturing.' },
  { id: 'design', label: 'Design', emoji: '🎨', blurb: 'Agents for the design and selections phase.' },
  { id: 'build', label: 'Build', emoji: '🏗️', blurb: 'Agents for production, scheduling, and job updates.' },
  { id: 'office', label: 'Office', emoji: '💼', blurb: 'Agents for back-office, finance, and admin work.' },
];

/** The five item lists every agent carries, in display order. */
export const AGENT_SECTIONS = [
  {
    key: 'routines',
    label: 'Routines',
    emoji: '🔁',
    titlePlaceholder: 'e.g. Every weekday at 8am',
    detailPlaceholder: 'What the agent does when this routine runs',
  },
  {
    key: 'abilities',
    label: 'Abilities',
    emoji: '⚡',
    titlePlaceholder: 'e.g. Drafts follow-up emails',
    detailPlaceholder: 'How it does it (optional)',
  },
  {
    key: 'library',
    label: 'Library',
    emoji: '📚',
    titlePlaceholder: 'e.g. Pricing sheet',
    detailPlaceholder: 'Link or notes on what this reference is',
  },
  {
    key: 'skills',
    label: 'Skills',
    emoji: '✨',
    titlePlaceholder: 'e.g. Estimate builder',
    detailPlaceholder: 'What the skill is used for (optional)',
  },
  {
    key: 'examples',
    label: 'Examples',
    emoji: '💬',
    titlePlaceholder: 'e.g. Weekly lead summary',
    detailPlaceholder: 'Example prompt, output, or scenario',
  },
];

export const AGENT_SECTION_KEYS = AGENT_SECTIONS.map((s) => s.key);

export const newAgentItemId = () => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;

/** Normalize an item list from Firestore (drops blanks, guarantees ids). */
export function normalizeAgentItems(items) {
  return (Array.isArray(items) ? items : [])
    .filter((it) => it && String(it.title || '').trim())
    .map((it) => ({
      id: it.id || newAgentItemId(),
      title: String(it.title || '').trim(),
      detail: String(it.detail || '').trim(),
      ...(it.skillId ? { skillId: it.skillId } : {}),
    }));
}

function sectionFields(data) {
  return AGENT_SECTION_KEYS.reduce((acc, key) => ({ ...acc, [key]: normalizeAgentItems(data?.[key]) }), {});
}

function sanitizeFileName(name) {
  return name.replace(/[^a-zA-Z0-9._-]/g, '_');
}

// ---- Department (tab) emojis ----

export async function getAgentGroupEmojis() {
  try {
    const snap = await getDoc(doc(db, SETTINGS_COLLECTION, SETTINGS_CONFIG_ID));
    return snap.exists() ? snap.data().groupEmojis || {} : {};
  } catch (e) {
    console.warn('Agent group emojis load:', e);
    return {};
  }
}

/** CRM: save one department's emoji (pass '' to reset to the default). */
export async function saveAgentGroupEmoji(tabId, emoji) {
  await setDoc(
    doc(db, SETTINGS_COLLECTION, SETTINGS_CONFIG_ID),
    { groupEmojis: { [tabId]: emoji || '' }, updatedAt: serverTimestamp() },
    { merge: true }
  );
}

// ---- Agents ----

function rowFromDoc(d) {
  const data = d.data();
  return { id: d.id, ...data, ...sectionFields(data) };
}

/**
 * List agents, optionally for one department (manual sortOrder wins, unplaced
 * newest first). Filtered client-side so no composite index is needed.
 */
export async function listAgentTemplates(category) {
  const snap = await getDocs(collection(db, COLLECTION));
  const rows = snap.docs.map(rowFromDoc).filter((r) => !category || r.category === category);
  return sortAutomationRows(rows);
}

export async function getAgentTemplate(agentId) {
  const snap = await getDoc(doc(db, COLLECTION, agentId));
  if (!snap.exists()) throw new Error('Agent not found.');
  return rowFromDoc(snap);
}

export async function createAgentTemplate({ name, description = '', category = 'lead', gifFile = null, sections = {}, user = {} }) {
  if (!String(name || '').trim()) throw new Error('Enter an agent name.');
  const docRef = await addDoc(collection(db, COLLECTION), {
    name: name.trim(),
    description: description.trim(),
    category,
    gif: null,
    ...sectionFields(sections),
    version: '1.0',
    authorName: user.name || 'BBA Team',
    createdAt: new Date(),
    updatedAt: new Date(),
  });
  if (gifFile) await uploadAgentGif(docRef.id, gifFile, user);
  return docRef.id;
}

/** Save name / description / department and any of the item lists. */
export async function updateAgentTemplate(agentId, fields) {
  const patch = { updatedAt: new Date() };
  if (fields.name !== undefined) patch.name = String(fields.name).trim();
  if (fields.description !== undefined) patch.description = String(fields.description).trim();
  if (fields.category !== undefined) patch.category = fields.category;
  AGENT_SECTION_KEYS.forEach((key) => {
    if (fields[key] !== undefined) patch[key] = normalizeAgentItems(fields[key]);
  });
  await updateDoc(doc(db, COLLECTION, agentId), patch);
  return patch;
}

/**
 * Upload the agent's display GIF and point the doc at it. Old files stay in
 * Storage — version snapshots may reference them; deleteAgentTemplate cleans up.
 */
export async function uploadAgentGif(agentId, file, user = {}) {
  if (!file) throw new Error('Choose a GIF file.');
  const isGif = file.type === 'image/gif' || file.name.toLowerCase().endsWith('.gif');
  if (!isGif) throw new Error('The agent image must be a .gif file.');
  if (file.size > MAX_GIF_BYTES) throw new Error('GIF must be 15MB or smaller.');

  const storagePath = `technology-community/agents/${user.uid || 'crm'}/${Date.now()}_${sanitizeFileName(file.name)}`;
  const snapshot = await uploadBytes(ref(storage, storagePath), file, { contentType: 'image/gif' });
  const url = await getDownloadURL(snapshot.ref);
  const gif = { url, storagePath, fileName: file.name, size: file.size };
  await updateDoc(doc(db, COLLECTION, agentId), { gif, updatedAt: new Date() });
  return gif;
}

export async function removeAgentGif(agentId) {
  await updateDoc(doc(db, COLLECTION, agentId), { gif: null, updatedAt: new Date() });
}

/** Persist a department's display order (portal shows the same order). */
export async function saveAgentsOrder(orderedIds) {
  const batch = writeBatch(db);
  orderedIds.forEach((id, idx) => {
    batch.update(doc(db, COLLECTION, id), { sortOrder: idx });
  });
  await batch.commit();
}

// ---- Version history (snapshots of the whole agent doc) ----

const versionsRef = (agentId) => collection(db, COLLECTION, agentId, 'versions');

function versionEntryFromDoc(data, { version, notes, user }) {
  return {
    version: String(version).trim(),
    name: data.name || '',
    description: data.description || '',
    category: data.category || '',
    gif: data.gif || null,
    ...sectionFields(data),
    savedByUid: user?.uid || '',
    savedByName: user?.name || 'Unknown',
    savedAt: new Date().toISOString(),
    notes: notes || '',
  };
}

async function seedInitialVersionIfNeeded(agentId, parentData) {
  const snap = await getDocs(versionsRef(agentId));
  if (!snap.empty) return;
  await addDoc(
    versionsRef(agentId),
    versionEntryFromDoc(parentData, {
      version: parentData.version || '1.0',
      notes: 'Initial version',
      user: { name: parentData.authorName || 'BBA Team' },
    })
  );
}

/** Snapshot the agent's current state as a new version and bump the doc's version. */
export async function saveAgentTemplateVersion({ agentId, version, notes = '', user = {} }) {
  if (!version || !String(version).trim()) throw new Error('A version number is required.');
  const mainRef = doc(db, COLLECTION, agentId);
  const snap = await getDoc(mainRef);
  if (!snap.exists()) throw new Error('Agent not found.');
  const data = snap.data();

  await seedInitialVersionIfNeeded(agentId, data);
  await updateDoc(mainRef, { version: String(version).trim(), updatedAt: serverTimestamp() });
  await addDoc(versionsRef(agentId), versionEntryFromDoc(data, { version, notes, user }));
}

/** Delete an agent with its versions and any GIF files no longer referenced. */
export async function deleteAgentTemplate(agentId) {
  const mainRef = doc(db, COLLECTION, agentId);
  const [snap, versions] = await Promise.all([getDoc(mainRef), getDocs(versionsRef(agentId))]);
  const paths = new Set();
  if (snap.exists() && snap.data().gif?.storagePath) paths.add(snap.data().gif.storagePath);
  versions.docs.forEach((v) => {
    const p = v.data()?.gif?.storagePath;
    if (p) paths.add(p);
  });
  await Promise.all(versions.docs.map((d) => deleteDoc(d.ref)));
  await deleteDoc(mainRef);
  await Promise.all(
    [...paths].map(async (p) => {
      try {
        await deleteObject(ref(storage, p));
      } catch (e) {
        console.warn('Agent GIF delete:', e);
      }
    })
  );
}
